import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  Send,
  Plus,
  ArrowLeft,
  RefreshCw,
  X,
  MailPlus,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

function StatCard({ icon: Icon, label, value, loading }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
          <Icon className="h-5 w-5 text-indigo-600" />
        </div>
        <span className="text-sm font-medium text-slate-500">{label}</span>
      </div>
      <p className="text-3xl font-bold text-slate-900">
        {loading ? (
          <span className="inline-block h-8 w-16 bg-slate-100 rounded animate-pulse" />
        ) : (
          value?.toLocaleString() ?? '—'
        )}
      </p>
    </div>
  );
}

function ClinicDetailPanel({ clinicId, onClose }) {
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [confirmRemoveUser, setConfirmRemoveUser] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');

  const refreshClinic = async () => {
    try {
      const data = await api.getSuperAdminClinicDetail(clinicId);
      setClinic(data);
    } catch (err) {
      // ignore refresh errors
    }
  };

  const handleRemoveUser = async (member) => {
    setRemovingUserId(member.id);
    try {
      await api.removeSuperAdminClinicMember(clinicId, member.id);
      toast.success(`${member.full_name || member.email} has been removed`);
      await refreshClinic();
    } catch (err) {
      toast.error(err.message || 'Failed to remove user');
    } finally {
      setRemovingUserId(null);
      setConfirmRemoveUser(null);
    }
  };

  const handleResendInvite = async (email) => {
    setResending(email);
    try {
      await api.resendClinicInvite(clinicId, email);
      toast.success(`Invite resent to ${email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setResending(null);
    }
  };

  const handleResetClinic = async () => {
    setResetting(true);
    try {
      const result = await api.resetClinic(clinicId);
      const counts = result.deleted || {};
      const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
      toast.success(`Clinic reset complete — ${total} record${total !== 1 ? 's' : ''} deleted`);
      await refreshClinic();
    } catch (err) {
      toast.error(err.message || 'Failed to reset clinic');
    } finally {
      setResetting(false);
      setShowResetConfirm(false);
      setResetConfirmText('');
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await api.getSuperAdminClinicDetail(clinicId);
        if (!cancelled) setClinic(data);
      } catch (err) {
        if (!cancelled) toast.error('Failed to load clinic details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [clinicId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <div className="h-6 w-48 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-4 w-full bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!clinic) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <h2 className="text-lg font-semibold text-slate-900">{clinic.name}</h2>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            clinic.status === 'active' ? 'bg-green-50 text-green-700' :
            clinic.status === 'pending' ? 'bg-amber-50 text-amber-700' :
            'bg-slate-100 text-slate-600'
          }`}>
            {clinic.status}
          </span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-sm text-slate-500">Patients</p>
          <p className="text-2xl font-bold text-slate-900">{clinic.patient_count?.toLocaleString()}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-4">
          <p className="text-sm text-slate-500">Team Members</p>
          <p className="text-2xl font-bold text-slate-900">{clinic.team_members?.length || 0}</p>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Clinic ID</span>
          <span className="text-slate-700 font-mono text-xs">{clinic.clinic_id}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Created</span>
          <span className="text-slate-700">{clinic.created_at ? new Date(clinic.created_at).toLocaleDateString() : '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Onboarding</span>
          <span className="text-slate-700">{clinic.onboarding_complete ? 'Complete' : 'Incomplete'}</span>
        </div>
      </div>

      {/* Team Members */}
      {clinic.team_members?.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-900 mb-3">Team Members</h3>
          <div className="space-y-2">
            {clinic.team_members.map((member) => (
              <div key={member.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900">{member.full_name || member.email}</p>
                  <p className="text-xs text-slate-500">{member.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {member.status === 'invited' && (
                    <button
                      onClick={() => handleResendInvite(member.email)}
                      disabled={resending === member.email}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors disabled:opacity-50"
                      title="Resend invite email"
                    >
                      <MailPlus className="h-3.5 w-3.5" />
                      {resending === member.email ? 'Sending...' : 'Resend'}
                    </button>
                  )}
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    member.status === 'invited' ? 'bg-amber-50 text-amber-700' :
                    member.status === 'active' ? 'bg-green-50 text-green-700' :
                    'bg-white text-slate-500'
                  }`}>
                    {member.status}
                  </span>
                  <span className="text-xs font-medium text-slate-500 bg-white px-2 py-1 rounded">
                    {member.role}
                  </span>
                  <button
                    onClick={() => setConfirmRemoveUser(member)}
                    disabled={removingUserId === member.id}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Remove user from clinic"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {removingUserId === member.id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {confirmRemoveUser && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Remove Team Member</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove <span className="font-medium text-slate-900">{confirmRemoveUser.full_name || confirmRemoveUser.email}</span>? They will be unable to log in.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setConfirmRemoveUser(null)}
                disabled={removingUserId === confirmRemoveUser.id}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveUser(confirmRemoveUser)}
                disabled={removingUserId === confirmRemoveUser.id}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {removingUserId === confirmRemoveUser.id ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="mt-6 border border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <h3 className="text-sm font-semibold text-red-700">Danger Zone</h3>
        </div>
        <h4 className="text-sm font-medium text-slate-900 mb-1">Reset Clinic Data</h4>
        <p className="text-sm text-slate-500 mb-4">
          Delete all patients, campaigns, and logs. Clinic settings, team, and rules will be preserved. The onboarding wizard will appear on next login.
        </p>
        <button
          onClick={() => setShowResetConfirm(true)}
          className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 hover:bg-red-50 rounded-lg transition-colors"
        >
          Reset Clinic...
        </button>
      </div>

      {/* Reset Clinic Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Reset Clinic Data</h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-slate-600">
                This will permanently delete all <span className="font-medium text-slate-900">patients, campaigns, outreach logs, import runs, and access logs</span> for this clinic.
              </p>
              <p className="text-sm text-slate-600">
                Clinic settings, team members, and rules will be preserved.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Type <span className="font-semibold text-slate-900">{clinic.name}</span> to confirm
                </label>
                <input
                  type="text"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={clinic.name}
                  disabled={resetting}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => { setShowResetConfirm(false); setResetConfirmText(''); }}
                disabled={resetting}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetClinic}
                disabled={resetting || resetConfirmText !== clinic.name}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {resetting ? 'Resetting...' : 'Reset Clinic'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteClinicModal({ isOpen, onClose, onCreated }) {
  const [clinicName, setClinicName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clinicName.trim() || !adminEmail.trim()) return;

    setSubmitting(true);
    try {
      await api.createClinicWithAdmin({
        clinic_name: clinicName.trim(),
        admin_email: adminEmail.trim(),
      });
      toast.success('Clinic created successfully');
      onCreated();
      onClose();
      setClinicName('');
      setAdminEmail('');
    } catch (err) {
      toast.error(err.message || 'Failed to create clinic');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Invite New Clinic</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Clinic Name</label>
            <input
              type="text"
              value={clinicName}
              onChange={(e) => setClinicName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="e.g. Springfield Medical"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Admin Email</label>
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="admin@clinic.com"
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Clinic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const { clinicId: detailClinicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => {
    return location.hash === '#clinics' ? 'clinics' : 'overview';
  });
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState(detailClinicId || null);

  // Sync tab with hash
  useEffect(() => {
    setActiveTab(location.hash === '#clinics' ? 'clinics' : 'overview');
  }, [location.hash]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const data = await api.getSuperAdminStats();
      setStats(data);
    } catch (err) {
      toast.error('Failed to load platform stats');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadClinics = useCallback(async () => {
    setLoadingClinics(true);
    try {
      const data = await api.getSuperAdminClinics();
      setClinics(data.clinics || []);
    } catch (err) {
      toast.error('Failed to load clinics');
    } finally {
      setLoadingClinics(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadClinics();
  }, [loadStats, loadClinics]);

  // If we arrived at /admin/clinics/:clinicId, show the detail panel
  useEffect(() => {
    if (detailClinicId) {
      setSelectedClinicId(detailClinicId);
      setActiveTab('clinics');
    }
  }, [detailClinicId]);

  const handleRefresh = () => {
    loadStats();
    loadClinics();
  };

  const handleClinicCreated = () => {
    loadClinics();
    loadStats();
  };

  const handleClinicClick = (clinicId) => {
    setSelectedClinicId(clinicId);
    navigate(`/admin/clinics/${clinicId}`);
  };

  const handleCloseDetail = () => {
    setSelectedClinicId(null);
    navigate('/admin#clinics');
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Manage clinics and monitor platform health</p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button
          onClick={() => { setActiveTab('overview'); navigate('/admin'); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'overview'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => { setActiveTab('clinics'); navigate('/admin#clinics'); }}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'clinics'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Clinics
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={Building2} label="Total Clinics" value={stats?.total_clinics} loading={loadingStats} />
          <StatCard icon={Users} label="Total Patients" value={stats?.total_patients} loading={loadingStats} />
          <StatCard icon={Send} label="Outreach This Month" value={stats?.outreach_this_month} loading={loadingStats} />
        </div>
      )}

      {/* Clinics Tab */}
      {activeTab === 'clinics' && (
        <div>
          {selectedClinicId ? (
            <ClinicDetailPanel clinicId={selectedClinicId} onClose={handleCloseDetail} />
          ) : (
            <>
              {/* Actions bar */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-500">
                  {loadingClinics ? 'Loading...' : `${clinics.length} clinic${clinics.length !== 1 ? 's' : ''}`}
                </p>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Invite Clinic
                </button>
              </div>

              {/* Clinics table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Clinic</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Patients</th>
                      <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Team</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingClinics ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i}>
                          <td className="px-4 py-3"><div className="h-4 w-40 bg-slate-100 rounded animate-pulse" /></td>
                          <td className="px-4 py-3"><div className="h-4 w-16 bg-slate-100 rounded animate-pulse" /></td>
                          <td className="px-4 py-3"><div className="h-4 w-12 bg-slate-100 rounded animate-pulse ml-auto" /></td>
                          <td className="px-4 py-3"><div className="h-4 w-8 bg-slate-100 rounded animate-pulse ml-auto" /></td>
                          <td className="px-4 py-3"><div className="h-4 w-24 bg-slate-100 rounded animate-pulse" /></td>
                        </tr>
                      ))
                    ) : clinics.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">
                          No clinics yet. Click "Invite Clinic" to add one.
                        </td>
                      </tr>
                    ) : (
                      clinics.map((clinic) => (
                        <tr
                          key={clinic.clinic_id}
                          onClick={() => handleClinicClick(clinic.clinic_id)}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-slate-900">{clinic.name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              clinic.status === 'active' ? 'bg-green-50 text-green-700' :
                              clinic.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {clinic.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-slate-700">
                            {clinic.patient_count?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-slate-700">
                            {clinic.team_count}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500">
                            {clinic.created_at ? new Date(clinic.created_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <InviteClinicModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onCreated={handleClinicCreated}
      />
    </div>
  );
}
